import * as React from 'react';
import image1 from '../images/SampleImage(1).png';
import image2 from '../images/SampleImage(2).png';
import image3 from '../images/SampleImage(3).png';
import image4 from '../images/SampleImage(4).png';
import image5 from '../images/SampleImage(5).png';
import image6 from '../images/SampleImage(6).png';
import cncImage from '../images/3axis.png';
import latheImage from '../images/lathes.png';
import sarikaProfile from '../images/SarikaProfile.png';
import nipunProfile from '../images/NipunProfile.png';

const API_BASE = process.env.REACT_APP_API_URL || 'https://alphatnc-5138977eb927.herokuapp.com';

// Matches today's hardcoded copy — shown immediately (no loading flash) and kept if the
// fetch fails or the field hasn't been edited/seeded yet. Capabilities has no server-side
// default (the server can't reference client-bundled assets) — this local fallback is the
// only place those default images/specs live for the public site.
export const DEFAULT_CONTENT = {
    heroHeadline: 'Custom Machining CNC Shop',
    aboutText:
        'Alpha Tools and components is a full-service, multifunctional cnc machine shop specializing in everything from prototype development to manufacturing, post-production, and delivery of machined parts.\n\nBased in Barrie, we bring over 25 years of experience and expertise to every project.\n\nServing both Canada and the United States, our highly skilled team is committed to delivering precision, quality, and efficiency at every stage of the process.',
    testimonials: [
        {
            id: 'default-testimonial',
            quote:
                'First of all, I want to say the customer service at Alpha was amazing. Walked in off the street with a milling question, the owner I presume took my cylinder and did the work right away. Great service. Would highly recommend.',
            author: 'Brandon Broadbridge',
            stars: 5,
        },
        {
            id: 'default-testimonial-2',
            quote: '',
            author: 'Sarika Ranjan',
            stars: 5,
            avatarUrl: sarikaProfile,
        },
        {
            id: 'default-testimonial-3',
            quote: '',
            author: 'Nipun Sharma',
            stars: 5,
            avatarUrl: nipunProfile,
        },
        {
            id: 'default-testimonial-4',
            quote: '',
            author: 'Abhiek Bist',
            stars: 5,
        },
    ],
    capabilities: [
        {
            id: 'default-milling',
            title: 'CNC Milling',
            subtitle: 'Daewoo DMV-4020',
            imageUrl: cncImage,
            specs: [
                { label: 'Machine Control', value: 'Fanuc 21i-MB' },
                { label: 'Table Size', value: '47" x 19.75"' },
                { label: 'Machine Travels (X/Y/Z)', value: '40/20/24' },
                { label: 'Spindle To Table Distance', value: '6" to 30"' },
                { label: 'Table Capacity', value: '1760 LBS' },
                { label: 'Spindle Taper', value: 'BT-40' },
                { label: 'Spindle Speeds', value: '8000 RPM' },
                { label: 'Tool Changer Style', value: 'Side Mount, Twin Arm' },
                { label: 'Tool Changer Capacity', value: '24' },
            ],
        },
        {
            id: 'default-turning',
            title: 'Turning',
            subtitle: 'First V700',
            imageUrl: latheImage,
            specs: [
                { label: 'Machine Control', value: 'FANUC Oi Mate-MD' },
                { label: 'Table Size', value: '31.49" x 14.75"' },
                { label: 'Machine Travels (X/Y/Z)', value: '27.5/15.7/21.6' },
                { label: 'Table Capacity', value: '551 LBS' },
                { label: 'Spindle Taper', value: 'BT-40' },
                { label: 'Spindle Speeds', value: '10000 RPM' },
                { label: 'Tool Changer Capacity', value: '24' },
            ],
        },
    ],
    gallery: [
        { url: image1, alt: 'CNC machined part 1', width: 679, height: 640 },
        { url: image2, alt: 'CNC machined part 2', width: 679, height: 640 },
        { url: image3, alt: 'CNC machined part 3', width: 679, height: 640 },
        { url: image4, alt: 'CNC machined part 4', width: 679, height: 640 },
        { url: image5, alt: 'CNC machined part 5', width: 679, height: 640 },
        { url: image6, alt: 'CNC machined part 6', width: 679, height: 640 },
    ],
};

const SiteContentContext = React.createContext(DEFAULT_CONTENT);

function isNonEmpty(value) {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim().length > 0;
    return Boolean(value);
}

// Merges fetched content over the defaults field-by-field, so an empty/unseeded field
// (e.g. gallery: [] before the first photo is ever uploaded) doesn't blank out the site.
// Exported so the dashboard's live preview (EditContent.js) shows the same fallback as
// the public site — it fetches /api/content independently, outside the Context/Provider.
export function mergeWithDefaults(fetched) {
    return {
        heroHeadline: isNonEmpty(fetched.heroHeadline) ? fetched.heroHeadline : DEFAULT_CONTENT.heroHeadline,
        aboutText: isNonEmpty(fetched.aboutText) ? fetched.aboutText : DEFAULT_CONTENT.aboutText,
        testimonials: isNonEmpty(fetched.testimonials) ? fetched.testimonials : DEFAULT_CONTENT.testimonials,
        capabilities: isNonEmpty(fetched.capabilities) ? fetched.capabilities : DEFAULT_CONTENT.capabilities,
        gallery: isNonEmpty(fetched.gallery) ? fetched.gallery : DEFAULT_CONTENT.gallery,
    };
}

export function SiteContentProvider({ children }) {
    const [content, setContent] = React.useState(DEFAULT_CONTENT);

    React.useEffect(() => {
        let cancelled = false;
        fetch(`${API_BASE}/api/content`)
            .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to load content'))))
            .then((data) => {
                if (!cancelled) setContent(mergeWithDefaults(data));
            })
            .catch(() => {
                // Keep showing DEFAULT_CONTENT — the site must never appear broken because
                // the content API is slow, down, or not configured yet.
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return <SiteContentContext.Provider value={content}>{children}</SiteContentContext.Provider>;
}

export function useSiteContent() {
    return React.useContext(SiteContentContext);
}
